import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { DEFAULT_ADDRESS, type ShopAddress, formatFullAddress } from "@/lib/shop-address";

export const Route = createFileRoute("/admin/settings")({
  head: () => ({
    meta: [
      { title: "Site Settings — Admin" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: SettingsPage,
});

function SettingsPage() {
  const [address, setAddress] = useState<ShopAddress>(DEFAULT_ADDRESS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from("site_settings")
        .select("shop_name, street, city, state, postal_code")
        .maybeSingle();
      if (data) setAddress(data as ShopAddress);
      setLoading(false);
    })();
  }, []);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setMsg(null);
    const { error } = await supabase
      .from("site_settings")
      .update(address)
      .eq("id", true);
    setSaving(false);
    setMsg(error ? `Error: ${error.message}` : "Saved. Contact page will update on next load.");
  };

  return (
    <div className="flex min-h-screen flex-col bg-cream">
      <SiteHeader />
      <main id="main-content" className="mx-auto w-full max-w-3xl flex-1 px-4 py-12 lg:px-8">
        <div className="mb-6 flex items-center justify-between">
          <h1 className="font-display text-3xl font-bold text-navy-deep">Site Settings</h1>
          <Link to="/admin" className="text-sm uppercase tracking-wider text-navy-deep/70 hover:text-red">← Back to Admin</Link>
        </div>

        <form onSubmit={save} className="space-y-5 border-2 border-navy-deep/10 bg-white p-6 shadow-card">
          <div>
            <h2 className="font-display text-xl font-bold text-navy-deep">Shop Address</h2>
            <p className="mt-1 text-sm text-navy-deep/70">
              This address powers the contact page map embed, "Get Directions" link, and shop info card.
              Changes take effect immediately on next page load.
            </p>
          </div>

          {loading ? (
            <div className="text-sm text-navy-deep/60">Loading…</div>
          ) : (
            <>
              <Field label="Business Name" value={address.shop_name} onChange={(v) => setAddress({ ...address, shop_name: v })} />
              <Field label="Street" value={address.street} onChange={(v) => setAddress({ ...address, street: v })} />
              <div className="grid gap-4 sm:grid-cols-[1fr_120px_140px]">
                <Field label="City" value={address.city} onChange={(v) => setAddress({ ...address, city: v })} />
                <Field label="State" value={address.state} onChange={(v) => setAddress({ ...address, state: v })} />
                <Field label="ZIP" value={address.postal_code} onChange={(v) => setAddress({ ...address, postal_code: v })} />
              </div>

              <div className="border-t border-navy-deep/10 pt-4">
                <div className="text-xs uppercase tracking-wider text-navy-deep/60">Preview</div>
                <div className="mt-1 text-sm text-navy-deep">{address.shop_name}</div>
                <div className="text-sm text-navy-deep/80">{formatFullAddress(address)}</div>
              </div>

              <div className="flex items-center gap-3">
                <button
                  type="submit"
                  disabled={saving}
                  className="bg-red px-6 py-3 font-display text-sm font-bold uppercase tracking-wider text-white shadow-md transition hover:bg-red-deep disabled:opacity-50"
                >
                  {saving ? "Saving…" : "Save Address"}
                </button>
                {msg && <span className="text-sm text-navy-deep/80">{msg}</span>}
              </div>
            </>
          )}
        </form>
      </main>
      <SiteFooter />
    </div>
  );
}

function Field({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <label className="block">
      <span className="block text-xs font-bold uppercase tracking-wider text-navy-deep/70">{label}</span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full border-2 border-navy-deep/20 bg-white px-3 py-2 text-navy-deep focus:border-navy-deep focus:outline-none"
      />
    </label>
  );
}
